/**
 * ViewRegistry — the single authority over extension view visibility.
 *
 * Two orthogonal dimensions govern extensions:
 *   A) runtime state (loaded/active…) — owned by ExtensionManager
 *   B) view presentation state — owned HERE, per view (not per extension):
 *        'registered'   placement known from the manifest, no DOM
 *        'materialized' host DOM exists + provider rendered, hidden
 *        'visible'      shown in its slot
 *
 * The ONLY way to change visibility is `reveal(viewId, source)` /
 * `hide(viewId, source)`, and every source is recorded and emitted
 * (`shell.view.revealed` / `shell.view.hidden`). The `shell.revealView`
 * core capability is the public surface over this method — user
 * gestures (ActivityBar / panel tab clicks), extensions
 * (`context.ui.reveal`), agents, and MCP clients all converge on it.
 *
 * Extension `activate()` never shows UI: it registers a ViewProvider
 * and the shell materializes it lazily on first reveal.
 *
 * Legacy compatibility: extensions not yet migrated to view providers
 * still render imperatively inside a bound command. For those,
 * `reveal()` falls back to executing `placement.fallbackCommand` after
 * showing the slot — the same behavior the ActivityBar click handler
 * had before, now behind the single audited choke point.
 */

import { emitShellEvent } from '@punica/editor';

export type ViewSlotId =
  | 'primarySidebar'
  | 'secondarySidebar'
  | 'contentSidebar'
  | 'content'
  | 'panel';

export type ViewPresentationState = 'registered' | 'materialized' | 'visible';

/**
 * How bad is the worst thing a view currently holds — mirrors
 * `shell.Views.ViewStatusLevel` in the substrate's public types.
 */
export type ViewStatusLevel =
  | 'none'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

/**
 * A view's passive indicator, published by the owning extension. Carries no
 * count: the number already lives wherever that extension renders it (a
 * status-bar counter, a bell badge), and `detail` gives the tab strip the words
 * for its tooltip and accessible name.
 */
export interface ViewStatus {
  level: ViewStatusLevel;
  detail?: string;
}

export interface ViewPlacement {
  viewId: string;
  slot: ViewSlotId;
  extensionName: string;
  title?: string;
  icon?: string;
  order?: number;
  /**
   * TRANSITIONAL — command executed on reveal when the owning extension
   * has not registered a ViewProvider yet (pre-migration extensions
   * render inside their open-command). Removed once the fleet migrates.
   */
  fallbackCommand?: string;
  /**
   * Panel-only: when true, no tab is created at load. `reveal()` creates
   * the tab on demand and `hide()` removes it, so the owning extension
   * controls when the tab is present in the strip.
   */
  onDemand?: boolean;
  /**
   * Panel-only: toolbar actions, carried on the placement so the tab can
   * be (re)created with its toolbar when reveal opens it on demand.
   */
  panelToolbar?: unknown[];
  /**
   * Relocation: the dock areas this view may be moved to. Resolved by the
   * analysis decorator (home slot ∪ manifest `relocatableTo` ∪ the default
   * panel↔secondarySidebar matrix). `moveView` validates against this set.
   */
  allowedSlots?: ViewSlotId[];
  /**
   * Relocation: the manifest-declared "home" slot. `slot` is the live
   * (possibly overridden) location; moving back to `homeSlot` clears the
   * persisted override.
   */
  homeSlot?: ViewSlotId;
}

export interface ViewProviderDisposable {
  dispose(): void;
}

export interface ViewProvider {
  /**
   * Render the view's content into the shell-provided host element.
   * Called at most once per materialization; the optional disposable is
   * invoked when the view is unmounted (extension deactivation).
   */
  render(
    host: HTMLElement
  ): void | ViewProviderDisposable | Promise<void | ViewProviderDisposable>;
}

export interface RevealSource {
  kind:
    'user-gesture' | 'capability' | 'extension' | 'session-restore' | 'host';
  /** Attribution id: invocation trace id, extension name, command id… */
  id?: string;
}

export interface RevealOptions {
  focus?: boolean;
}

export interface ViewInfo {
  viewId: string;
  slot: ViewSlotId;
  extensionName: string;
  title?: string;
  state: ViewPresentationState;
}

type LayoutManagerInternal = {
  materializeViewHost(slotId: string, viewId: string): HTMLElement;
  showView(slotId: string, viewId: string): void;
  hideSlotViews(slotId: string): void;
  removeViewHost?(slotId: string, viewId: string): void;
  setSlotCollapsed?(slotId: string, collapsed: boolean): void;
};

function getLayoutManager(): LayoutManagerInternal | undefined {
  return (globalThis as any).punica?.shell?.Layout?.manager as
    LayoutManagerInternal | undefined;
}

function getExtensionManagerAny(): any {
  return (globalThis as any).punica?.extensions?.manager;
}

function getCommandsManager(): any {
  return (globalThis as any).punica?.kernel?.Commands?.manager;
}

function getPanelTabsAny(): any {
  return (globalThis as any).punica?.shell?.PanelTabs;
}

function getPanelContainers(): any {
  return (globalThis as any).punica?.shell?.PanelContainers;
}

/**
 * The tabbed container for a slot (bottom horizontal / right vertical), or
 * null for slots without one. Falls back to the legacy bottom `PanelTabs` when
 * the PanelContainers registry is absent (older shells).
 */
function getContainerForSlot(slot: string): any {
  const container = getPanelContainers()?.forSlot?.(slot);
  if (container) return container;
  if (slot === 'panel') return getPanelTabsAny();
  return null;
}

function getPanelLayoutAny(): any {
  return (globalThis as any).punica?.shell?.PanelLayout?.manager;
}

function getActivityBarManager(): any {
  return (globalThis as any).punica?.shell?.ActivityBar?.manager;
}

export class ViewRegistryManager {
  #placements: Map<string, ViewPlacement> = new Map();
  #providers: Map<string, ViewProvider> = new Map();
  /** Provider render results by viewId — presence marks 'materialized'. */
  #rendered: Map<string, ViewProviderDisposable | null> = new Map();
  /** Currently visible view per slot. */
  #visibleBySlot: Map<ViewSlotId, string> = new Map();
  /**
   * Passive per-view indicator (what the view's list holds). Kept here rather
   * than on the tab strip because a view can be relocated between docks: the
   * status belongs to the view, and whichever container holds it renders it.
   */
  #status: Map<string, ViewStatus> = new Map();

  /**
   * Register a view placement. Called during the analysis (load) phase
   * from the Views decorator — the manifest declares WHERE a view
   * lives; code and visibility come later.
   */
  registerPlacement(placement: ViewPlacement): void {
    if (!placement?.viewId || !placement.slot) return;
    const existing = this.#placements.get(placement.viewId);
    if (existing && existing.extensionName !== placement.extensionName) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Views] Placement collision for view "${placement.viewId}" between "${existing.extensionName}" and "${placement.extensionName}". Keeping the last registered.`
      );
    }
    this.#placements.set(placement.viewId, placement);
    emitShellEvent({
      name: 'shell.view.registered',
      source: 'shell:views',
      payload: {
        viewId: placement.viewId,
        slot: placement.slot,
        extensionName: placement.extensionName
      }
    });
  }

  /**
   * Register a render provider for a declared view. Called by extension
   * code during activation via `context.ui.registerViewProvider`.
   * Registration alone never shows anything.
   */
  registerProvider(
    viewId: string,
    provider: ViewProvider,
    extensionName?: string
  ): ViewProviderDisposable {
    if (!this.#placements.has(viewId)) {
      // Allow provider-first registration (placement may arrive from a
      // late-loaded manifest), but flag it — a view with no placement
      // can never be revealed.
      // eslint-disable-next-line no-console
      console.warn(
        `[Views] Provider registered for "${viewId}" but no placement is declared in any manifest.` +
          (extensionName ? ` (extension: ${extensionName})` : '')
      );
    }
    this.#providers.set(viewId, provider);
    return {
      dispose: () => {
        if (this.#providers.get(viewId) === provider) {
          this.#providers.delete(viewId);
        }
      }
    };
  }

  getPlacement(viewId: string): ViewPlacement | undefined {
    return this.#placements.get(viewId);
  }

  hasProvider(viewId: string): boolean {
    return this.#providers.has(viewId);
  }

  getState(viewId: string): ViewPresentationState | undefined {
    if (!this.#placements.has(viewId) && !this.#providers.has(viewId)) {
      return undefined;
    }
    const placement = this.#placements.get(viewId);
    if (placement && this.#visibleBySlot.get(placement.slot) === viewId) {
      return 'visible';
    }
    return this.#rendered.has(viewId) ? 'materialized' : 'registered';
  }

  getVisibleView(slot: ViewSlotId): string | undefined {
    return this.#visibleBySlot.get(slot);
  }

  listViews(): ViewInfo[] {
    return Array.from(this.#placements.values()).map((p) => ({
      viewId: p.viewId,
      slot: p.slot,
      extensionName: p.extensionName,
      title: p.title,
      state: this.getState(p.viewId) ?? 'registered'
    }));
  }

  /**
   * Reveal a view — the single visibility mutator of the substrate.
   *
   * Flow: resolve placement → activate the owning extension if needed
   * (reason 'view') → materialize host (visibility-neutral) → render
   * provider once → show the slot → sync ActivityBar/panel tab → emit
   * `shell.view.revealed` with the source.
   */
  async reveal(
    viewId: string,
    source: RevealSource,
    options?: RevealOptions
  ): Promise<void> {
    const placement = this.#placements.get(viewId);
    if (!placement) {
      throw new Error(
        `[Views] Cannot reveal "${viewId}": no placement is registered for this view.`
      );
    }

    // 1) Make sure the owning extension is running — interactive reason,
    //    because a reveal is a deliberate act of showing UI.
    if (!this.#providers.has(viewId)) {
      const mgr = getExtensionManagerAny();
      if (mgr?.activate) {
        try {
          await mgr.activate(placement.extensionName, {
            reason: 'view',
            trigger: viewId,
            presentation: 'interactive'
          });
        } catch (err) {
          // An extension whose module graph throws on load (a circular import
          // leaving a const in the temporal dead zone is the shipped example:
          // extension-docker 1.12.0) never reaches `registerViewProvider`.
          // Rethrowing aborted the reveal before step 4, so the slot was never
          // shown and the click looked like it did nothing at all — a whole
          // panel dying with no message anywhere. Carry on instead: the slot
          // opens, empty and visibly selected, and the reason is on the record.
          // eslint-disable-next-line no-console
          console.error(
            `[Views] activation of "${placement.extensionName}" failed while revealing "${viewId}":`,
            err
          );
        }
      }
    }

    // 2) Materialize the host without any visibility side effects.
    const layout = getLayoutManager();
    if (!layout) {
      throw new Error('[Views] Layout manager unavailable — cannot reveal.');
    }
    const host = layout.materializeViewHost(placement.slot, viewId);

    // 3) Render the provider. Durable slots render exactly once per
    //    materialization; the contentSidebar drawer is TRANSIENT — its
    //    content is parameterized per open (pending-state pattern) and
    //    its DOM is reset wholesale when the primary sidebar switches,
    //    so the provider re-renders on every reveal.
    const provider = this.#providers.get(viewId);
    const transient = placement.slot === 'contentSidebar';
    if (provider && (transient || !this.#rendered.has(viewId))) {
      if (transient) {
        const previous = this.#rendered.get(viewId);
        try {
          previous?.dispose();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[Views] provider dispose failed for "${viewId}":`, err);
        }
      }
      let result: unknown;
      try {
        result = await provider.render(host);
      } catch (err) {
        // Same rule as activation above: a provider that throws must not cost
        // the user the reveal. Most render entries empty the host before they
        // build, so rethrowing here left a slot that was both blank AND
        // hidden.
        // eslint-disable-next-line no-console
        console.error(
          `[Views] provider render failed for "${viewId}" (${placement.extensionName}):`,
          err
        );
        result = null;
      }
      this.#rendered.set(
        viewId,
        result && typeof (result as any).dispose === 'function'
          ? (result as ViewProviderDisposable)
          : null
      );
    }

    // 4) Show it. Revealing any view re-expands a collapsed split pane
    //    (no-op unless we previously collapsed this slot).
    layout.showView(placement.slot, viewId);
    layout.setSlotCollapsed?.(placement.slot, false);
    this.#visibleBySlot.set(placement.slot, viewId);

    const container = getContainerForSlot(placement.slot);
    if (container) {
      // Tabbed dock areas (bottom panel + right sidebar) share a tab strip.
      // On-demand / freshly-relocated views have no tab yet, so create it here
      // (idempotent for tabs already open — open() with autoActivate:false is a
      // no-op when the entry exists), then sync the visual selection without
      // re-entering the legacy command path.
      container.open?.({
        id: viewId,
        title: placement.title ?? viewId,
        commandId: placement.fallbackCommand ?? '',
        iconClass: placement.icon,
        order: placement.order,
        autoActivate: false,
        toolbar: placement.panelToolbar
      });
      container.activateTabVisual?.(viewId);
      this.#paintStatus(viewId, placement.slot);
    }
    if (placement.slot === 'primarySidebar') {
      try {
        getActivityBarManager()?.setActiveByViewId?.(viewId);
      } catch {
        /* best-effort */
      }
    }

    // 5) TRANSITIONAL: pre-migration extensions render inside their
    //    bound command, not a provider. Execute it so the first reveal
    //    actually paints content. Deleted once the fleet migrates.
    if (!provider && placement.fallbackCommand) {
      try {
        getCommandsManager()?.executeCommand?.(placement.fallbackCommand);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Views] fallback command "${placement.fallbackCommand}" for view "${viewId}" failed:`,
          err
        );
      }
    }

    emitShellEvent({
      name: 'shell.view.revealed',
      source: 'shell:views',
      payload: {
        viewId,
        slot: placement.slot,
        extensionName: placement.extensionName,
        source: source?.kind ?? 'host',
        sourceId: source?.id,
        focus: options?.focus ?? false
      }
    });
  }

  /**
   * Hide a view. When the view is the one showing in its slot, the slot's
   * fate depends on what else lives there: a tabbed dock (bottom panel, right
   * sidebar) that still holds other tabs stays open on the first remaining
   * sibling, and only a dock left with no tab collapses. Hiding one view is
   * a statement about that view, not about the region — the rule `moveView`
   * already applies when it takes the visible view out of a dock.
   *
   * Until 1.13.1 every non-drawer slot collapsed unconditionally, which was
   * written for a dock showing a single view and, on a shared dock, closed
   * the whole right sidebar when a notebook's on-demand Variables tab hid
   * itself beside the debugger, the terminal and the assistant.
   */
  hide(viewId: string, source: RevealSource): void {
    const placement = this.#placements.get(viewId);
    if (!placement) return;
    if (this.#visibleBySlot.get(placement.slot) !== viewId) return;

    const layout = getLayoutManager();
    let promote: string | null = null;
    if (placement.slot === 'contentSidebar') {
      // Transient drawer: the overlay collapses through the host CSS
      // `#contentSidebar:empty` rule, so hiding must UNMOUNT every host
      // in the slot — a display:none host would keep the (empty) drawer
      // slid in. Providers re-render on the next reveal anyway.
      this.#unmountSlotHosts(placement.slot);
    } else {
      layout?.hideSlotViews(placement.slot);
      const container = getContainerForSlot(placement.slot);
      // On-demand views remove their tab from the strip when hidden, so
      // the dock area is clean again until the activity re-reveals it.
      // Silent: the container's own close would promote a sibling through
      // the legacy command path; the registry promotes below, through
      // `reveal`, so every invariant (provider render-once, ActivityBar
      // sync, headless block) holds for the promoted view too.
      if (placement.onDemand) {
        container?.close?.(viewId, { silent: true });
      }
      const remaining = (container?.listTabs?.() ?? []).filter(
        (t: { id: string; userHidden: boolean }) =>
          !t.userHidden && t.id !== viewId
      );
      if (remaining.length > 0) {
        promote = remaining[0].id;
      } else {
        // Nothing left in the region: collapse its split pane so the panel /
        // side pane (and its gutter) fully disappear rather than leaving an
        // empty tab strip and reserved space. No-op for overlay slots.
        layout?.setSlotCollapsed?.(placement.slot, true);
      }
    }
    this.#visibleBySlot.delete(placement.slot);

    if (promote) {
      const sibling = promote;
      if (this.#placements.has(sibling)) {
        void this.reveal(sibling, source, { focus: false }).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn(
            `[Views] failed to promote sibling "${sibling}" in "${placement.slot}" after hiding "${viewId}":`,
            err
          );
        });
      } else {
        // TRANSITIONAL: a tab with no placement is a pre-migration tab that
        // renders inside its bound command; the container's own activation
        // is the only path that paints it.
        getContainerForSlot(placement.slot)?.setActive?.(sibling);
      }
    }

    if (placement.slot === 'primarySidebar') {
      try {
        getActivityBarManager()?.setActiveByViewId?.(null);
      } catch {
        /* best-effort */
      }
    }

    emitShellEvent({
      name: 'shell.view.hidden',
      source: 'shell:views',
      payload: {
        viewId,
        slot: placement.slot,
        source: source?.kind ?? 'host',
        sourceId: source?.id
      }
    });
  }

  /**
   * Publish a view's passive indicator and paint it on the container that
   * currently holds the view.
   *
   * Passive by construction: nothing here reveals, activates or re-orders
   * anything, so an extension reporting "2 warnings" can never steal the user's
   * panel. `owner` is the calling extension's name (supplied by
   * `context.ui.setViewStatus`); a mismatch is refused so one extension cannot
   * decorate another's tab.
   */
  setViewStatus(viewId: string, status: ViewStatus, owner?: string): void {
    const placement = this.#placements.get(viewId);
    if (!placement) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Views] setViewStatus for unknown view "${viewId}" ignored${owner ? ` (${owner})` : ''}.`
      );
      return;
    }
    if (owner && placement.extensionName && owner !== placement.extensionName) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Views] "${owner}" may not set the status of view "${viewId}" (owned by "${placement.extensionName}").`
      );
      return;
    }
    const level = status?.level ?? 'none';
    if (level === 'none') {
      this.#status.delete(viewId);
    } else {
      this.#status.set(viewId, { level, detail: status?.detail });
    }
    this.#paintStatus(viewId, placement.slot);
  }

  getViewStatus(viewId: string): ViewStatus | undefined {
    return this.#status.get(viewId);
  }

  /**
   * Hand the stored status to the container for `slot`. Called on every publish
   * and again after this registry opens a tab (reveal / relocation), so an
   * on-demand or freshly moved tab is born already tinted — the strip never has
   * to read back from here.
   */
  #paintStatus(viewId: string, slot: ViewSlotId): void {
    try {
      getContainerForSlot(slot)?.setTabStatus?.(
        viewId,
        this.#status.get(viewId) ?? null
      );
    } catch {
      /* decoration is best-effort; it must never break a reveal */
    }
  }

  /**
   * Relocate a view between dock areas (bottom panel ↔ right sidebar) — the
   * relocation primitive behind the `shell.moveView` capability and the
   * cross-area drag-drop. Validates the target against `allowedSlots`, tears
   * down the old slot's tab + host, moves the placement, persists the override,
   * and — when the view was visible — re-reveals it in the destination
   * container (landing in that strip's orientation). Routing the visible case
   * back through `reveal` preserves every invariant (ActivityBar sync, headless
   * block, provider render-once).
   */
  async moveView(
    viewId: string,
    targetSlot: ViewSlotId,
    source: RevealSource
  ): Promise<void> {
    const placement = this.#placements.get(viewId);
    if (!placement) {
      throw new Error(
        `[Views] Cannot move "${viewId}": no placement is registered.`
      );
    }
    const allowed = placement.allowedSlots ?? [placement.slot];
    if (!allowed.includes(targetSlot)) {
      throw new Error(
        `[Views] "${viewId}" cannot be moved to "${targetSlot}". Allowed slots: ${allowed.join(
          ', '
        )}.`
      );
    }
    const oldSlot = placement.slot;
    if (oldSlot === targetSlot) return;

    const wasVisible = this.#visibleBySlot.get(oldSlot) === viewId;
    const layout = getLayoutManager();

    // 1) Tear down the old slot's tab + rendered host.
    const oldContainer = getContainerForSlot(oldSlot);
    oldContainer?.close?.(viewId, { silent: true });
    const disposable = this.#rendered.get(viewId);
    try {
      disposable?.dispose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[Views] provider dispose failed for "${viewId}":`, err);
    }
    this.#rendered.delete(viewId);
    layout?.removeViewHost?.(oldSlot, viewId);

    // If the moved view was the visible one, the old dock now has no active
    // view. Promote a remaining (non-hidden) sibling and KEEP the dock open;
    // only collapse it when the moved view was the last tab there.
    let promoteInOldSlot: string | null = null;
    if (wasVisible) {
      this.#visibleBySlot.delete(oldSlot);
      const remaining = (oldContainer?.listTabs?.() ?? []).filter(
        (t: { id: string; userHidden: boolean }) =>
          !t.userHidden && t.id !== viewId
      );
      if (remaining.length > 0) {
        promoteInOldSlot = remaining[0].id;
      } else {
        layout?.setSlotCollapsed?.(oldSlot, true);
      }
    }

    // 2) Move the placement + persist the override (cleared when back to home).
    placement.slot = targetSlot;
    try {
      await getPanelLayoutAny()?.setSlotOverride?.(
        viewId,
        targetSlot,
        placement.homeSlot ?? oldSlot
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Views] failed to persist slot override for "${viewId}":`,
        err
      );
    }

    // 3) Re-place in the destination. Visible → re-reveal (re-materializes,
    //    re-renders, opens + activates the tab on the new container). Hidden →
    //    just move the persistent tab across (on-demand views have no tab).
    if (wasVisible) {
      await this.reveal(viewId, source);
    } else if (!placement.onDemand) {
      getContainerForSlot(targetSlot)?.open?.({
        id: viewId,
        title: placement.title ?? viewId,
        commandId: placement.fallbackCommand ?? '',
        iconClass: placement.icon,
        order: placement.order,
        autoActivate: false,
        toolbar: placement.panelToolbar
      });
      this.#paintStatus(viewId, targetSlot);
    }

    // Show the promoted sibling in the source dock so it stays populated
    // instead of collapsing out from under the user.
    if (promoteInOldSlot) {
      try {
        await this.reveal(promoteInOldSlot, source);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Views] failed to promote sibling "${promoteInOldSlot}" in "${oldSlot}":`,
          err
        );
      }
    }

    emitShellEvent({
      name: 'shell.view.moved',
      source: 'shell:views',
      payload: {
        viewId,
        fromSlot: oldSlot,
        toSlot: targetSlot,
        source: source?.kind ?? 'host',
        sourceId: source?.id
      }
    });
  }

  /**
   * The Layout manager wiped every host in a slot wholesale — today only
   * the contextual contentSidebar drawer, which resets when the primary
   * sidebar switches. Sync bookkeeping so the registry doesn't believe
   * views are still materialized/visible in DOM that no longer exists:
   * dispose provider results, forget materialization, clear visibility.
   */
  notifySlotReset(slot: ViewSlotId): void {
    this.#unmountSlotHosts(slot);

    const visible = this.#visibleBySlot.get(slot);
    if (visible !== undefined) {
      this.#visibleBySlot.delete(slot);
      emitShellEvent({
        name: 'shell.view.hidden',
        source: 'shell:views',
        payload: {
          viewId: visible,
          slot,
          source: 'host',
          sourceId: 'slot-reset'
        }
      });
    }
  }

  /**
   * Dispose provider results, forget materialization and drop the DOM
   * hosts for every view in a slot. Placements stay. Used for the
   * transient contentSidebar drawer (hide = unmount) and for syncing
   * after a Layout-side wipe (where removeViewHost is then a no-op).
   */
  #unmountSlotHosts(slot: ViewSlotId): void {
    const layout = getLayoutManager();
    for (const placement of this.#placements.values()) {
      if (placement.slot !== slot) continue;
      const disposable = this.#rendered.get(placement.viewId);
      try {
        disposable?.dispose();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Views] provider dispose failed for "${placement.viewId}":`,
          err
        );
      }
      this.#rendered.delete(placement.viewId);
      layout?.removeViewHost?.(slot, placement.viewId);
    }
  }

  /**
   * Unmount everything owned by an extension — used by
   * `ExtensionManager.deactivate()`. Manifest placements STAY (the
   * ActivityBar item remains clickable and will re-activate), providers
   * and rendered DOM go away.
   */
  unmountByExtension(extensionName: string): void {
    for (const placement of this.#placements.values()) {
      if (placement.extensionName !== extensionName) continue;
      const viewId = placement.viewId;

      if (this.#visibleBySlot.get(placement.slot) === viewId) {
        this.hide(viewId, { kind: 'host', id: 'deactivate' });
      }

      const disposable = this.#rendered.get(viewId);
      try {
        disposable?.dispose();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[Views] provider dispose failed for "${viewId}":`, err);
      }
      this.#rendered.delete(viewId);
      this.#providers.delete(viewId);

      const layout = getLayoutManager() as any;
      layout?.removeViewHost?.(placement.slot, viewId);
    }
  }
}

const views = {
  manager: new ViewRegistryManager()
};

export default views;
